/**
 * NsectionB1Page
 * B.1.說明（A6.4-FORM-METH-002 Section 1. Introduction）
 * 單一 Card，表格形式呈現 Methodology key elements
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB1Service, { NsectionB1Detail, NsectionB1Update } from '../services/nsectionB1Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

const NsectionB1Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB1Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ghgTypes, setGhgTypes] = useState<SystemCode[]>([]);
  const [mitigationTypes, setMitigationTypes] = useState<SystemCode[]>([]);

  const [formData, setFormData] = useState({
    ghg_mitigation_types: [] as number[],
    mitigation_outcome_type: null as number | null,
    is_reversal_risk: false,
    eligible_projects: '',
  });

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB1Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    Promise.all([
      getSystemCodesByType('A6.4_ghg_mitigation_type'),
      getSystemCodesByType('project_type'),
    ]).then(([g, m]) => { setGhgTypes(g); setMitigationTypes(m); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setFormData({ ghg_mitigation_types: [], mitigation_outcome_type: null, is_reversal_risk: false, eligible_projects: '' });
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB1Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setFormData({
            ghg_mitigation_types: result.ghg_mitigation_types || [],
            mitigation_outcome_type: result.mitigation_outcome_type,
            is_reversal_risk: result.is_reversal_risk,
            eligible_projects: result.eligible_projects || '',
          });
        } else {
          setData(null);
          setFormData({ ghg_mitigation_types: [], mitigation_outcome_type: null, is_reversal_risk: false, eligible_projects: '' });
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const updateData: NsectionB1Update = {
        ghg_mitigation_types: formData.ghg_mitigation_types,
        mitigation_outcome_type: formData.mitigation_outcome_type,
        is_reversal_risk: formData.is_reversal_risk,
        eligible_projects: formData.eligible_projects || undefined,
      };
      const result = await nsectionB1Service.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b1', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b1', 'update');

  const rowStyle: React.CSSProperties = { display: 'flex', borderBottom: '1px solid #f0f0f0', padding: '16px 0' };
  const labelCellStyle: React.CSSProperties = { width: '30%', fontWeight: 600, fontSize: '14px', color: '#333', paddingRight: '16px', paddingTop: '4px' };
  const valueCellStyle: React.CSSProperties = { width: '70%' };

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b1" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            {isZh
              ? '以下表格呈現方法論的關鍵要素摘要：'
              : 'The following table presents a summary of the key elements of a methodology:'}
          </p>

          <div style={{
            width: '100%', background: '#fff', borderRadius: '10px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
              {isZh ? '表 1. 方法論關鍵要素' : 'Table 1. Methodology key elements'}
            </div>

            {/* Row 1: GHG 減緩措施類型 */}
            <div style={rowStyle}>
              <div style={labelCellStyle}>
                {isZh ? 'GHG 減緩措施類型' : 'Type of GHG mitigation measure(s)'}
              </div>
              <div style={valueCellStyle}>
                {ghgTypes.map(g => (
                  <label key={g.id} style={{ display: 'block', marginBottom: '6px', fontSize: '13px', cursor: canUpdate ? 'pointer' : 'default' }}>
                    <input type="checkbox" disabled={!canUpdate}
                      checked={formData.ghg_mitigation_types.includes(g.id)}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...formData.ghg_mitigation_types, g.id]
                          : formData.ghg_mitigation_types.filter(id => id !== g.id);
                        setFormData({ ...formData, ghg_mitigation_types: types });
                      }}
                      style={{ marginRight: '8px' }} />
                    {isZh ? (g.code_cname || g.code_ename) : (g.code_ename || g.code_cname)}
                  </label>
                ))}
              </div>
            </div>

            {/* Row 2: 減緩成果類型 */}
            <div style={rowStyle}>
              <div style={labelCellStyle}>
                {isZh ? '減緩成果類型' : 'Types of mitigation outcomes achieved under this methodology'}
              </div>
              <div style={valueCellStyle}>
                <select style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', fontSize: '14px' }}
                  value={formData.mitigation_outcome_type || ''} disabled={!canUpdate}
                  onChange={(e) => setFormData({ ...formData, mitigation_outcome_type: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{isZh ? '請選擇' : 'Select...'}</option>
                  {mitigationTypes.map(m => (
                    <option key={m.id} value={m.id}>{isZh ? (m.code_cname || m.code_ename) : (m.code_ename || m.code_cname)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: 是否有逆轉風險 */}
            <div style={rowStyle}>
              <div style={labelCellStyle}>
                {isZh ? '是否有逆轉風險' : 'Are the mitigation outcomes under this methodology at risk of reversal?'}
              </div>
              <div style={{ ...valueCellStyle, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  onClick={() => { if (canUpdate) setFormData({ ...formData, is_reversal_risk: !formData.is_reversal_risk }); }}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px',
                    background: formData.is_reversal_risk ? '#4CAF50' : '#ccc',
                    position: 'relative', cursor: canUpdate ? 'pointer' : 'default', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '2px',
                    left: formData.is_reversal_risk ? '22px' : '2px',
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </div>
                <span style={{ fontSize: '14px', color: formData.is_reversal_risk ? '#4CAF50' : '#999' }}>
                  {formData.is_reversal_risk ? (isZh ? '有風險 (Yes)' : 'Yes') : (isZh ? '無風險 (No)' : 'No')}
                </span>
              </div>
            </div>

            {/* Row 4: 適用專案描述 */}
            <div style={{ ...rowStyle, borderBottom: 'none', flexDirection: 'column' }}>
              <div style={{ ...labelCellStyle, width: '100%', marginBottom: '8px' }}>
                {isZh ? '適用專案描述' : 'Typical projects eligible under the methodology'}
              </div>
              <div style={{ width: '100%' }}>
                <RichTextEditor
                  value={formData.eligible_projects}
                  onChange={(html) => setFormData({ ...formData, eligible_projects: html })}
                  disabled={!canUpdate}
                  placeholder={isZh ? '請描述適用於此方法論的典型專案類型...' : 'Describe typical projects eligible under this methodology...'}
                  minHeight="150px"
                />
              </div>
            </div>

            {/* 儲存按鈕 */}
            {canUpdate && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '8px' }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                  {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB1Page;
